import {
  useCallback,
  useRef,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
} from "react";

import {
  PannellumPanorama,
  type PannellumPanoramaProps,
} from "./PannellumPanorama";
import type { PanoramaTarget, PannellumViewer } from "../panorama/pannellum";

export interface PanoramaShellContext {
  viewerRef: MutableRefObject<PannellumViewer | null>;
  lookAt: (target: PanoramaTarget) => void;
}

export interface PanoramaShellProps
  extends Omit<PannellumPanoramaProps, "sceneId" | "imageUrl" | "viewerRef"> {
  sceneId: string | number;
  imageUrl?: string | null;
  fallbackUrl?: string | null;
  fallbackTitle?: string;
  fallbackAllow?: string;
  viewerRef?: MutableRefObject<PannellumViewer | null>;
  rootClassName?: string;
  rootStyle?: CSSProperties;
  loadingFallback?: ReactNode;
  overlay?: ReactNode | ((context: PanoramaShellContext) => ReactNode);
}

const DEFAULT_FALLBACK_ALLOW =
  "vr; xr; accelerometer; magnetometer; gyroscope; webvr; webxr; xr-spatial-tracking; autoplay; camera; microphone";

export const PanoramaShell = ({
  sceneId,
  imageUrl,
  fallbackUrl,
  fallbackTitle = "Interactive panorama",
  fallbackAllow = DEFAULT_FALLBACK_ALLOW,
  viewerRef: providedViewerRef,
  rootClassName = "",
  rootStyle,
  loadingFallback,
  overlay,
  pitchOffset = 0,
  ...panoramaProps
}: PanoramaShellProps) => {
  const internalViewerRef = useRef<PannellumViewer | null>(null);
  const viewerRef = providedViewerRef ?? internalViewerRef;

  const lookAt = useCallback(
    (target: PanoramaTarget) => {
      viewerRef.current?.lookAt(
        target.pitch + pitchOffset,
        target.yaw,
        undefined,
        target.duration ?? 750,
      );
    },
    [pitchOffset, viewerRef],
  );

  const context: PanoramaShellContext = { viewerRef, lookAt };
  const overlayContent =
    typeof overlay === "function" ? overlay(context) : overlay;

  return (
    <div
      className={`iv-panorama-shell ${rootClassName}`.trim()}
      style={rootStyle}
      data-tour-id="iv-panorama-shell"
    >
      {imageUrl ? (
        <PannellumPanorama
          {...panoramaProps}
          sceneId={sceneId}
          imageUrl={imageUrl}
          viewerRef={viewerRef}
          pitchOffset={pitchOffset}
        />
      ) : fallbackUrl ? (
        <iframe
          src={fallbackUrl}
          title={fallbackTitle}
          className="iv-panorama-shell__fallback"
          allowFullScreen
          allow={fallbackAllow}
        />
      ) : (
        (loadingFallback ?? (
          <div
            className="iv-panorama-shell__loading"
            role="status"
            aria-live="polite"
          >
            Loading panorama…
          </div>
        ))
      )}
      {overlayContent}
    </div>
  );
};
